/**
 * DEMO DATA — the call-centre simulator.
 *
 * ⚠️  PHASE 1 (CLIENT DEMO) ONLY. Real data arrives next phase. See ../demo-mode.ts.
 *
 * Models one shift of a small contact centre and advances it one second at a
 * time, so the dashboard's live panels move the way they would under real
 * traffic: calls arrive, ring, get answered, run for a while, then end and land
 * in the historical rollups.
 *
 * DESIGN CONSTRAINTS THAT SHAPED THIS
 * -----------------------------------
 * 1. NO RANDOMNESS AT RENDER TIME. Everything random happens inside `tick()`
 *    and is written into state. React may re-render many times per second, and
 *    a `Math.random()` in a render path would make the numbers visibly jitter
 *    and re-sort under the client's cursor.
 *
 * 2. SEEDED PRNG, not `Math.random`. The demo must be reproducible — if a
 *    number looks wrong while presenting, it has to be reproducible afterwards.
 *
 * 3. THE ROSTER IS REAL. `tick` is given the live agent list and simulates
 *    activity on top of those identities, so the client sees their own people.
 *
 * 4. CONSERVATION. Every counter the dashboard shows is derived from the same
 *    completed-call ledger. Answered + abandoned always equals total, per
 *    interval and in aggregate, because they are counted from one array rather
 *    than generated independently — independent generation is how demo data
 *    ends up self-contradictory on screen.
 */
import type {
  AgentStatus,
  SupervisorAgentSummary,
  SupervisorLiveCall,
} from '@kc3/types'
import { DEMO_RECORDINGS, type DemoRecording } from './recordings.js'

/* ── Seeded PRNG ───────────────────────────────────────────────────────── */

/** mulberry32 — small, fast, good enough for presentation data. */
function makeRng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const pick = <T,>(rng: () => number, xs: readonly T[]): T =>
  xs[Math.floor(rng() * xs.length)] as T

const between = (rng: () => number, lo: number, hi: number): number =>
  Math.floor(lo + rng() * (hi - lo + 1))

/* ── Static reference data ─────────────────────────────────────────────── */

export const DEMO_QUEUES = [
  { queueId: 'demo-q-sales', queueName: 'Sales', slTargetSec: 20 },
  { queueId: 'demo-q-support', queueName: 'Customer Support', slTargetSec: 30 },
  { queueId: 'demo-q-billing', queueName: 'Billing', slTargetSec: 20 },
  { queueId: 'demo-q-retention', queueName: 'Retention', slTargetSec: 45 },
] as const

export const DEMO_CAMPAIGNS = [
  { campaignId: 'demo-c-inbound', campaignName: 'Inbound Service', dids: ['+14155550100', '+14155550101'] },
  { campaignId: 'demo-c-outbound', campaignName: 'Outbound Sales', dids: ['+14155550200'] },
  { campaignId: 'demo-c-winback', campaignName: 'Win-Back Q3', dids: [] as string[] },
] as const

/** Weighted so the mix looks like a real wrap-up distribution. */
const DEMO_DISPOSITIONS: ReadonlyArray<{ name: string; weight: number }> = [
  { name: 'Resolved', weight: 34 },
  { name: 'Sale Completed', weight: 14 },
  { name: 'Callback Scheduled', weight: 12 },
  { name: 'Information Provided', weight: 11 },
  { name: 'Escalated', weight: 8 },
  { name: 'No Answer', weight: 7 },
  { name: 'Wrong Number', weight: 5 },
  { name: 'Not Interested', weight: 5 },
  { name: 'Complaint Logged', weight: 4 },
]

/**
 * Simulated colleagues, added ALONGSIDE the real logged-in agents.
 *
 * A live tenant has one or two people signed in, which makes for a thin floor:
 * queue distribution, occupancy and the agent leaderboard all need a populated
 * team before they say anything. These fill it out so the dashboard reads the
 * way it would in production.
 *
 * Extensions start at 1010 to stay clear of the real 100x range, so a demo
 * agent can never be mistaken for — or collide with — a real one.
 */
const DEMO_EXTRA_AGENTS: ReadonlyArray<{ name: string; extension: string }> = [
  { name: 'Priya Sharma', extension: '1010' },
  { name: 'Daniel Okafor', extension: '1011' },
  { name: 'Mei Lin Tan', extension: '1012' },
  { name: 'Carlos Ramírez', extension: '1013' },
  { name: 'Aisha Rahman', extension: '1014' },
  { name: 'Tom Whitfield', extension: '1015' },
  { name: 'Sofia Almeida', extension: '1016' },
  { name: 'Hiroshi Nakamura', extension: '1017' },
  { name: 'Grace Mwangi', extension: '1018' },
  { name: 'Lukas Andersen', extension: '1019' },
  { name: 'Fatima Al-Zahra', extension: '1020' },
  { name: 'James Okoro', extension: '1021' },
]

/* ── Simulation state ──────────────────────────────────────────────────── */

/** A call that has finished — the ledger every historical metric reads from. */
export interface CompletedCall {
  callId: string
  agentId: string
  queueId: string
  campaignId: string
  direction: 'inbound' | 'outbound'
  /** Epoch ms the call arrived. */
  startedAt: number
  answered: boolean
  abandoned: boolean
  /** Seconds waited before answer (or before abandoning). */
  waitSec: number
  talkSec: number
  holdSec: number
  acwSec: number
  disposition: string
}

/** A call currently in progress. */
export interface ActiveCall {
  callId: string
  agentId: string
  agentName: string
  queueId: string
  campaignId: string
  callerCLI: string
  /** Epoch ms. */
  startedAt: number
  /** Epoch ms the agent picked up; null while still ringing in queue. */
  answeredAt: number | null
  /** How long this call will run once answered, in seconds. */
  plannedTalkSec: number
  waitSec: number
  recording: DemoRecording
}

interface AgentSim {
  id: string
  userId: string
  name: string
  email?: string
  extension?: string
  status: AgentStatus
  /** Epoch ms of the last status change. */
  since: number
  callsHandled: number
  talkSec: number
  /** Epoch ms this agent becomes eligible for another call. */
  busyUntil: number
}

export interface DemoState {
  agents: AgentSim[]
  active: ActiveCall[]
  completed: CompletedCall[]
  seq: number
  startedAt: number
  /**
   * ONE continuously-advanced PRNG for the whole run.
   *
   * It lives on the state rather than being re-created per tick because
   * mulberry32 is designed to be advanced from its own state, not re-seeded.
   * Re-seeding it each tick with an incrementing counter made consecutive
   * ticks' FIRST draw highly correlated, so the low-probability branches
   * (`rng() < 0.04`, the spontaneous state changes) fired on nearly every
   * tick — which reset every agent's `since` once a second and pinned the
   * "In State" column at 0:00.
   */
  rng: () => number
}

export interface RosterEntry {
  id: string
  userId: string
  name: string
  email?: string
  extension?: string
}

/* ── Bootstrap ─────────────────────────────────────────────────────────── */

/**
 * Seed the simulation with a backfilled shift.
 *
 * The history is generated BACKWARDS from now across `backfillHours` so the
 * interval pivot and the performance tables have something to show the instant
 * the page opens. Without this the client would watch an empty dashboard
 * slowly fill over several minutes.
 */
export function createDemoState(
  roster: RosterEntry[],
  now: number,
  backfillHours = 12,
  seed = 20260819,
): DemoState {
  const rng = makeRng(seed)

  // Real agents FIRST, then the simulated colleagues. Real identities keep
  // their own ids so anything keyed on them still lines up; demo agents get a
  // `demo-` prefix, which is also what marks them as not-real to anyone
  // reading the data later.
  const people: RosterEntry[] = [
    ...roster,
    ...DEMO_EXTRA_AGENTS.map((a, i) => ({
      id: `demo-agent-${i + 1}`,
      userId: `demo-user-${i + 1}`,
      name: a.name,
      email: `${a.name.toLowerCase().replace(/[^a-z]+/g, '.')}@demo.local`,
      extension: a.extension,
    })),
  ]

  const agents: AgentSim[] = people.map((p) => ({
    id: p.id,
    userId: p.userId,
    name: p.name,
    ...(p.email ? { email: p.email } : {}),
    ...(p.extension ? { extension: p.extension } : {}),
    status: 'IDLE' as AgentStatus,
    since: now - between(rng, 30, 900) * 1000,
    callsHandled: 0,
    talkSec: 0,
    busyUntil: 0,
  }))

  const state: DemoState = {
    agents,
    active: [],
    completed: [],
    seq: 0,
    startedAt: now,
    rng,
  }

  // Backfill. Volume follows a coarse daily curve so the interval pivot shows
  // a believable shape (busy mid-shift, quiet at the edges) rather than a flat
  // line, which is the tell-tale of generated data.
  const windowMs = backfillHours * 3600_000
  const from = now - windowMs

  for (let t = from; t < now; t += 60_000) {
    const hour = new Date(t).getUTCHours()
    const busyness = 0.35 + 0.65 * Math.sin((Math.max(0, hour - 5) / 14) * Math.PI)
    const arrivals = rng() < busyness * 0.9 ? between(rng, 1, 3) : 0

    for (let i = 0; i < arrivals; i++) {
      state.completed.push(makeCompletedCall(rng, agents, t, state.seq++))
    }
  }

  // Roll the backfilled totals onto the agents so their live counters agree
  // with the historical tables from the first frame.
  for (const c of state.completed) {
    if (!c.answered) continue
    const agent = agents.find((a) => a.id === c.agentId)
    if (!agent) continue
    agent.callsHandled += 1
    agent.talkSec += c.talkSec
  }

  // Open the shift with a plausible spread of live states.
  for (const agent of agents) {
    const roll = rng()
    if (roll < 0.15) setStatus(agent, 'PAUSED', now - between(rng, 20, 400) * 1000)
    else if (roll < 0.22) setStatus(agent, 'ON_BREAK', now - between(rng, 60, 600) * 1000)
    else setStatus(agent, 'IDLE', now - between(rng, 5, 300) * 1000)
  }

  return state
}

function setStatus(agent: AgentSim, status: AgentStatus, at: number): void {
  agent.status = status
  agent.since = at
}

function weightedDisposition(rng: () => number): string {
  const total = DEMO_DISPOSITIONS.reduce((s, d) => s + d.weight, 0)
  let r = rng() * total
  for (const d of DEMO_DISPOSITIONS) {
    r -= d.weight
    if (r <= 0) return d.name
  }
  return DEMO_DISPOSITIONS[0]!.name
}

function makeCompletedCall(
  rng: () => number,
  agents: AgentSim[],
  at: number,
  seq: number,
): CompletedCall {
  const queue = pick(rng, DEMO_QUEUES)
  const campaign = pick(rng, DEMO_CAMPAIGNS)
  const agent = pick(rng, agents)

  // ~7% abandon rate — above Ofcom's 3% ceiling on purpose, so the dashboard's
  // amber/red service-level banding is actually visible during the demo. A
  // flawless dataset makes the thresholds look decorative.
  const abandoned = rng() < 0.07
  const answered = !abandoned

  const waitSec = abandoned ? between(rng, 8, 90) : between(rng, 1, 55)
  const talkSec = answered ? between(rng, 35, 480) : 0
  const holdSec = answered && rng() < 0.3 ? between(rng, 5, 70) : 0
  const acwSec = answered ? between(rng, 8, 60) : 0

  return {
    callId: `demo-hist-${seq}`,
    agentId: agent.id,
    queueId: queue.queueId,
    campaignId: campaign.campaignId,
    direction: rng() < 0.72 ? 'inbound' : 'outbound',
    startedAt: at,
    answered,
    abandoned,
    waitSec,
    talkSec,
    holdSec,
    acwSec,
    disposition: answered ? weightedDisposition(rng) : 'Abandoned',
  }
}

/* ── The tick ──────────────────────────────────────────────────────────── */

/**
 * Advance the simulation to `now`.
 *
 * MUTATES `state` in place and returns it; the caller is responsible for
 * producing fresh snapshot arrays for React. Mutating is deliberate — cloning
 * a 12-hour ledger every second to satisfy immutability would allocate
 * megabytes a minute for no benefit, since nothing renders `state` directly.
 */
export function tickDemo(state: DemoState, now: number): DemoState {
  const rng = state.rng

  /* 1. Finish calls whose planned talk time has elapsed. */
  const stillActive: ActiveCall[] = []
  for (const call of state.active) {
    const answered = call.answeredAt !== null
    const elapsed = answered ? (now - (call.answeredAt as number)) / 1000 : 0

    if (answered && elapsed >= call.plannedTalkSec) {
      const agent = state.agents.find((a) => a.id === call.agentId)
      const acwSec = between(rng, 20, 75)
      const holdSec = rng() < 0.25 ? between(rng, 5, 60) : 0

      state.completed.push({
        callId: call.callId,
        agentId: call.agentId,
        queueId: call.queueId,
        campaignId: call.campaignId,
        direction: 'inbound',
        startedAt: call.startedAt,
        answered: true,
        abandoned: false,
        waitSec: call.waitSec,
        talkSec: Math.round(elapsed),
        holdSec,
        acwSec,
        disposition: weightedDisposition(rng),
      })

      if (agent) {
        agent.callsHandled += 1
        agent.talkSec += Math.round(elapsed)
        // Straight into wrap-up, like a real ACD — going idle instantly is one
        // of the things that makes simulated floors look fake.
        setStatus(agent, 'ACW', now)
        agent.busyUntil = now + acwSec * 1000
      }
      continue
    }

    // Ringing calls get answered after their queue wait elapses.
    if (!answered && (now - call.startedAt) / 1000 >= call.waitSec) {
      call.answeredAt = now
      const agent = state.agents.find((a) => a.id === call.agentId)
      if (agent) setStatus(agent, 'IN_CALL', now)
    }

    stillActive.push(call)
  }
  state.active = stillActive

  /* 2. Release agents whose wrap-up or pause has expired. */
  for (const agent of state.agents) {
    if (agent.busyUntil && now >= agent.busyUntil) {
      agent.busyUntil = 0
      if (agent.status === 'ACW' || agent.status === 'PAUSED') {
        setStatus(agent, 'IDLE', now)
      }
    }
  }

  /* 3. Occasional spontaneous state changes, so the floor breathes. */
  if (rng() < 0.012) {
    const idle = state.agents.filter((a) => a.status === 'IDLE')
    const candidate = idle.length > 0 ? pick(rng, idle) : undefined
    if (candidate) {
      const goBreak = rng() < 0.4
      setStatus(candidate, goBreak ? 'ON_BREAK' : 'PAUSED', now)
      candidate.busyUntil = now + between(rng, 120, 600) * 1000
    }
  }
  if (rng() < 0.015) {
    const away = state.agents.filter(
      (a) => a.status === 'ON_BREAK' || a.status === 'PAUSED',
    )
    const candidate = away.length > 0 ? pick(rng, away) : undefined
    if (candidate) {
      setStatus(candidate, 'IDLE', now)
      candidate.busyUntil = 0
    }
  }

  /* 4. New arrivals — only to agents who are genuinely free. */
  const free = state.agents.filter((a) => a.status === 'IDLE')
  const targetConcurrent = Math.max(1, Math.round(state.agents.length * 0.45))

  // 0.12 rather than 0.35: with a dozen agents the higher rate kept every one
  // of them permanently mid-transition, so the floor never looked settled and
  // no timer on screen ever accumulated.
  if (free.length > 0 && state.active.length < targetConcurrent && rng() < 0.12) {
    const agent = pick(rng, free)
    const queue = pick(rng, DEMO_QUEUES)
    const campaign = pick(rng, DEMO_CAMPAIGNS)
    const recording = pick(rng, DEMO_RECORDINGS)
    const waitSec = between(rng, 2, 18)

    state.active.push({
      callId: `${recording.callId}::${state.seq++}`,
      agentId: agent.id,
      agentName: agent.name,
      queueId: queue.queueId,
      campaignId: campaign.campaignId,
      callerCLI: recording.callerCLI,
      startedAt: now,
      answeredAt: null,
      // NOT capped to the recording length. Several of the recordings are only
      // 13-26s, and capping to them made every call end within seconds — which
      // cycled agents RINGING → IN_CALL → ACW → IDLE continuously and reset
      // their "in state" timer to 0:00 every few seconds, so the column never
      // showed a real elapsed figure. The monitor loops its audio, so a call
      // may safely outlast the file behind it.
      plannedTalkSec: between(rng, 90, 420),
      waitSec,
      recording,
    })

    setStatus(agent, 'RINGING', now)
  }

  /* 5. Trim the ledger to the reporting window. Unbounded growth would turn a
        long-running demo into a memory leak and slow every aggregation. */
  const cutoff = now - 24 * 3600_000
  if (state.completed.length > 4000) {
    state.completed = state.completed.filter((c) => c.startedAt >= cutoff)
  }

  return state
}

/* ── Projections onto the dashboard's wire shapes ──────────────────────── */

export function toAgentSummaries(state: DemoState): SupervisorAgentSummary[] {
  return state.agents.map((a) => {
    const call = state.active.find((c) => c.agentId === a.id)
    const summary: SupervisorAgentSummary = {
      id: a.id,
      userId: a.userId,
      name: a.name,
      status: a.status,
      statusSince: new Date(a.since).toISOString(),
      callsHandled: a.callsHandled,
      callDurationSec: a.talkSec,
    }
    if (a.email) summary.email = a.email
    if (a.extension) summary.extension = a.extension
    if (call) {
      summary.currentCallId = call.callId
      summary.currentQueueId = call.queueId
    }
    return summary
  })
}

export function toLiveCalls(state: DemoState, now: number): SupervisorLiveCall[] {
  return state.active.map((c) => ({
    callId: c.callId,
    agentId: c.agentId,
    agentName: c.agentName,
    callerCLI: c.callerCLI,
    startedAt: new Date(c.startedAt).toISOString(),
    durationSec: Math.max(0, Math.floor((now - c.startedAt) / 1000)),
    status: 'ANSWERED' as const,
    queueId: c.queueId,
    channelId: `PJSIP/demo-${c.callId.slice(0, 8)}`,
  }))
}

/**
 * Apply a supervisor agent-action to the simulation.
 *
 * Keyed on `userId` to match the real path exactly: the gateway and worker
 * address agent presence by user id, not agent id, so the demo exercises the
 * same identifier the production code uses.
 *
 * A forced logout also drops any call the agent was on — leaving a live call
 * attached to a logged-out agent is precisely the inconsistent state the real
 * ACD avoids, and it would show up on the dashboard as a ghost row.
 */
export function applyDemoAgentAction(
  state: DemoState,
  action: 'force_pause' | 'force_logout',
  userId: string,
  now: number,
): void {
  const agent = state.agents.find((a) => a.userId === userId)
  if (!agent) return

  if (action === 'force_logout') {
    state.active = state.active.filter((c) => c.agentId !== agent.id)
    setStatus(agent, 'OFFLINE', now)
    // Keep them offline rather than letting the idle-release sweep pull them
    // straight back in on the next tick.
    agent.busyUntil = now + 120_000
    return
  }

  state.active = state.active.filter((c) => c.agentId !== agent.id)
  setStatus(agent, 'PAUSED', now)
  agent.busyUntil = now + 90_000
}
